import { ModalResult } from "@/components/modal/ModalResult.ts";
import { WModalContent } from "@/components/modal/WModal.tsx";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { AddMethodResult } from "@/modules/billing/choose_method/AddMethodParams.ts";
import { ChooseMethod, type ChooseMethodProps } from "@/modules/billing/choose_method/ChooseMethod.tsx";
import { useAddMethod, type UseAddMethodProps } from "@/modules/billing/choose_method/useAddMethod.tsx";
import { raiseBackgroundErrorToast } from "@/modules/notifications/toast.tsx";
import { Trans, useLingui } from "@lingui/react/macro";

export interface AddPaymentMethodModalProps extends UseAddMethodProps {
    account: EBillingAccount;
    onlyAutomated?: ChooseMethodProps["defaultOnlyAutomated"];
}

export default function AddPaymentMethodModal({ account, onlyAutomated, ...useProps }: AddPaymentMethodModalProps) {
    const { t } = useLingui();
    const adder = useAddMethod({ ...useProps, account });

    return (
        <WModalContent
            title={t`Add payment method`}
            label={t`Add`}
            footerNotice={!adder.hasSelectedMethod ? t`Please select an item in the list` : undefined}
            isActionDisabled={!adder.hasSelectedMethod}
            modalAction={async (ev) => {
                if (!(await adder.validate(ev))) {
                    return ModalResult.KEEP_OPEN;
                }

                try {
                    const result = await adder.addPaymentMethod({ account });
                    return result === AddMethodResult.SUCCESS ? ModalResult.CLOSE : ModalResult.KEEP_OPEN;
                } catch (e) {
                    raiseBackgroundErrorToast(e, <Trans>Error adding payment method</Trans>);
                    return ModalResult.KEEP_OPEN;
                }
            }}
        >
            <ChooseMethod hasAutomatedSwitch defaultOnlyAutomated={onlyAutomated} scrollToSelected {...adder.props} />
        </WModalContent>
    );
}
